import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { Validator, ValidationError } from 'class-validator';

import { AppStoreModule } from '@/store';
import { OnboardingRegistration, OnboardingRegistrationOdinAccount, CreateNewOdinAccount } from '@/common/models';
import IAdminService from '@/services/http/interfaces/IAdminService';
import { OnboardingRegistrationStatus } from '@/common/viewModels/Enums';
import { RegistrationDetailsForm, RegistrationDetailsOdinRefresh } from '@/common/viewModels';
import { RegistrationDetailsFormDataCloudAccount } from '@/common/viewModels/registrationDetails/form/data/RegistrationDetailsFormDataCloudAccount';
import { RegistrationsHelper } from '@/common/helpers/RegistrationsHelper';
import { RegistrationsDetailsFormStateCustomer } from '@/common/viewModels/registrationDetails/form/state/RegistrationDetailsFormStateCustomer';
import { RegistrationsDetailsFormStateCloudAccount } from '@/common/viewModels/registrationDetails/form/state/RegistrationDetailsFormStateCloudAccount';
import { RegistrationDetailsFormLookups } from '@/common/viewModels/registrationDetails/form/RegistrationDetailsFormLookups';
import { RegistrationsDetailsFormStateNewCloudAccount } from '@/common/viewModels/registrationDetails/form/state/RegistrationDetailsFormStateNewCloudAccount';
import { RegistrationDetailsFormDataNewCloudAccount } from '@/common/viewModels/registrationDetails/form/data/RegistrationDetailsFormDataNewCloudAccount';
import { FormLookupsBase } from '@/common/form/FormLookupsBase';

export interface IRegistrationDetailsState {
  onboardingRegistration: OnboardingRegistration | null;
  onboardingRegistrationOdinAccounts: OnboardingRegistrationOdinAccount[];
  form: RegistrationDetailsForm;
  odinRefresh: RegistrationDetailsOdinRefresh;
}

@Module({
  name: 'registrationDetails',
  namespaced: true,
})
export default class RegistrationDetailsStore extends VuexModule implements IRegistrationDetailsState {
  // State
  onboardingRegistration: OnboardingRegistration | null = null;
  onboardingRegistrationOdinAccounts: OnboardingRegistrationOdinAccount[] = [];
  form: RegistrationDetailsForm = new RegistrationDetailsForm();
  odinRefresh: RegistrationDetailsOdinRefresh = RegistrationsHelper.getOdinRefresh();

  // Getters
  get onboardingRegistrationOdinAccount(): OnboardingRegistrationOdinAccount | undefined {
    return this.onboardingRegistration ? this.onboardingRegistrationOdinAccounts.find((account) => account.accountId === this.onboardingRegistration!.odinAccountId) : undefined;
  }

  get isFormValid(): boolean {
    if (this.onboardingRegistration && this.onboardingRegistrationOdinAccounts) {
      const isCreateOrSyncInProgress: boolean = this.onboardingRegistration.isOdinCreationInProgress || this.onboardingRegistration.isOdinSyncInProgress;
      const isFormValid: boolean = this.onboardingRegistrationOdinAccounts.length > 0 ?
        // Single/Multi PBA
        (this.form.state.customer.isValid && this.form.state.cloudAccount.isValid) :
        // No PBA
        (this.form.state.customer.isValid && this.form.state.newCloudAccount.isValid);

      return !isCreateOrSyncInProgress && isFormValid;
    }

    return false;
  }

  // Mutations
  @Mutation
  SET_REGISTRATIONS_DETAILS(payload: { onboardingRegistration: OnboardingRegistration, onboardingRegistrationOdinAccounts: OnboardingRegistrationOdinAccount[] }) {
    this.onboardingRegistration = payload.onboardingRegistration;
    this.onboardingRegistrationOdinAccounts = payload.onboardingRegistrationOdinAccounts;
    this.odinRefresh = RegistrationsHelper.getOdinRefresh(payload.onboardingRegistration);
  }

  @Mutation
  UPDATE_CUSTOMER_FORM_FIELD_BY_NAME(payload: { fieldName: string, value: any }) {
    (this.form.data.customer as any)[payload.fieldName] = payload.value;
  }

  @Mutation
  async UPDATE_CUSTOMER_FORM_STATE() {
    await this.form.state.customer.validate(this.form.doValidation, this.form.data.customer);
  }

  @Mutation
  UPDATE_CLOUD_ACCOUNT_FORM_FIELD_BY_NAME(payload: { fieldName: string, value: any }) {
    (this.form.data.cloudAccount as any)[payload.fieldName] = payload.value;
  }

  @Mutation
  async UPDATE_CLOUD_ACCOUNT_FORM_STATE() {
    await this.form.state.cloudAccount.validate(this.form.doValidation, this.form.data.cloudAccount);
  }

  @Mutation
  LOAD_CUSTOMER_FORM(onboardingRegistration: OnboardingRegistration) {
    Object.assign(this.form.data.customer, {
      name: onboardingRegistration.name,
      phone: onboardingRegistration.phone,
      email: onboardingRegistration.email,
      tenantName: onboardingRegistration.tenantName,
      hasNoPba: this.onboardingRegistrationOdinAccounts.length === 0,
    });
    this.form.state.customer = new RegistrationsDetailsFormStateCustomer();
  }

  @Mutation
  LOAD_CLOUD_ACCOUNT_FORM(onboardingRegistrationOdinAccounts: OnboardingRegistrationOdinAccount[]) {

    this.form.data.cloudAccount = new RegistrationDetailsFormDataCloudAccount();
    this.form.state.cloudAccount = new RegistrationsDetailsFormStateCloudAccount();

    // Return if there are no Odin Accounts (No form required)
    if (onboardingRegistrationOdinAccounts.length === 0) { return; }

    const hasMultipleOdinAccounts: boolean = onboardingRegistrationOdinAccounts.length > 1;
    const onboardingRegistrationOdinAccount: OnboardingRegistrationOdinAccount = onboardingRegistrationOdinAccounts[0];

    this.form.data.cloudAccount.accountId = hasMultipleOdinAccounts ? '' : onboardingRegistrationOdinAccount.accountId;

    if (!hasMultipleOdinAccounts) {
      RegistrationsHelper.mapOdinAccountToExistingCloudAccount(this.form.data.cloudAccount, onboardingRegistrationOdinAccount);
    }
  }

  @Mutation
  UPDATE_CLOUD_ACCOUNT_FORM(accountId: any) {
    const onboardingRegistrationOdinAccount = this.onboardingRegistrationOdinAccounts.find((account) => account.accountId === accountId);
    if (onboardingRegistrationOdinAccount) {
      RegistrationsHelper.mapOdinAccountToExistingCloudAccount(this.form.data.cloudAccount, onboardingRegistrationOdinAccount);
    }
  }

  @Mutation
  SET_FORM_DO_VALIDATION(doValidation: boolean) {
    this.form.doValidation = doValidation;
  }

  @Mutation
  SET_ODIN_REFRESH_IS_DISABLED(isDisabled: boolean) {
    this.odinRefresh.isDisabled = isDisabled;
  }

  @Mutation
  UPDATE_NEW_CLOUD_ACCOUNT_FORM_FIELD_BY_NAME(payload: { fieldName: string, value: any }) {
    (this.form.data.newCloudAccount as any)[payload.fieldName] = payload.value;
  }

  @Mutation
  async UPDATE_NEW_CLOUD_ACCOUNT_FORM_STATE() {
    await this.form.state.newCloudAccount.validate(this.form.doValidation, this.form.data.newCloudAccount);
  }

  @Mutation
  GET_STATEPROVINCES_LOOKUP() {
    this.form.lookups.stateProvinces = FormLookupsBase.populate([
      { text: 'ACT', value: 'ACT' },
      { text: 'NSW', value: 'NSW' },
      { text: 'NT', value: 'NT' },
      { text: 'QLD', value: 'QLD' },
      { text: 'SA', value: 'SA' },
      { text: 'TAS', value: 'TAS' },
      { text: 'VIC', value: 'VIC' },
      { text: 'WA', value: 'WA' },
    ], 'text', 'value');
  }

  @Mutation
  GET_COUNTRIES_LOOKUP() {
    this.form.lookups.countries = FormLookupsBase.populate([
      { text: 'AU', value: 'au' },
    ], 'text', 'value');
  }

  @Mutation
  RESET_NEW_CLOUD_ACCOUNT_FORM() {
    this.form.data.newCloudAccount = Object.assign(new RegistrationDetailsFormDataNewCloudAccount(), {
      administrativeContactEmail: this.onboardingRegistration!.email,
      administrativeContactPhone: this.onboardingRegistration!.phone,
    });

    this.form.state.newCloudAccount = new RegistrationsDetailsFormStateNewCloudAccount();
  }

  // Actions
  @Action
  async getRegistrationDetails(payload: { adminService: IAdminService, registrationId: string }) {

    AppStoreModule.SET_ISLOADING(true);

    try {
      const onboardingRegistration: OnboardingRegistration = await payload.adminService.getOnboardingRegistration(payload.registrationId);

      const onboardingRegistrationOdinAccounts: OnboardingRegistrationOdinAccount[] =
        await payload.adminService.getOnboardingRegistrationOdinAccounts(payload.registrationId);

      this.SET_REGISTRATIONS_DETAILS({ onboardingRegistration, onboardingRegistrationOdinAccounts });

      if ([OnboardingRegistrationStatus.Submitted, OnboardingRegistrationStatus.Error].indexOf(onboardingRegistration.status) >= 0) {
        // Edit enabled
        this.LOAD_CUSTOMER_FORM(onboardingRegistration);
        this.LOAD_CLOUD_ACCOUNT_FORM(onboardingRegistrationOdinAccounts);
      }

      if (this.onboardingRegistrationOdinAccounts.length === 0) {
        this.RESET_NEW_CLOUD_ACCOUNT_FORM();
      }


    } catch (error) {
      AppStoreModule.SAVE_HTTP_ERROR('Unable to load registration');
    }

    AppStoreModule.SET_ISLOADING(false);

  }

  @Action
  async getOdinAccounts(payload: { adminService: IAdminService, registrationId: string, showLoader?: boolean }) {

    if (payload.showLoader) {
      AppStoreModule.SET_ISLOADING(true);
    }

    try {
      const onboardingRegistration: OnboardingRegistration = await payload.adminService.getOnboardingRegistration(payload.registrationId);

      const onboardingRegistrationOdinAccounts: OnboardingRegistrationOdinAccount[] =
        await payload.adminService.getOnboardingRegistrationOdinAccounts(payload.registrationId);

      this.SET_REGISTRATIONS_DETAILS({ onboardingRegistration, onboardingRegistrationOdinAccounts });

      this.LOAD_CLOUD_ACCOUNT_FORM(onboardingRegistrationOdinAccounts);

    } catch (error) {
      AppStoreModule.SAVE_HTTP_ERROR('Unable to load Odin accounts');
       // tslint:disable-next-line:no-console
      console.log(error);
    } finally {
      this.resetOdinSync(payload.adminService!);
      this.SET_ODIN_REFRESH_IS_DISABLED(false);
    }

    if (payload.showLoader) {
      AppStoreModule.SET_ISLOADING(false);
    }

  }

  @Action
  async customerFormInputChange(payload: { fieldName: string, value: any }) {
    this.UPDATE_CUSTOMER_FORM_FIELD_BY_NAME(payload);
    await this.UPDATE_CUSTOMER_FORM_STATE();
  }

  @Action
  async cloudAccountFormInputChange(payload: { fieldName: string, value: any }) {
    this.UPDATE_CLOUD_ACCOUNT_FORM_FIELD_BY_NAME(payload);
    await this.UPDATE_CLOUD_ACCOUNT_FORM_STATE();

    if (payload.fieldName === 'accountId') {
      this.UPDATE_CLOUD_ACCOUNT_FORM(payload.value);
      // Revalidate the Odin Registration for missing data
      await this.UPDATE_CLOUD_ACCOUNT_FORM_STATE();
    }
  }

  @Action
  async requestCancel(adminService: IAdminService): Promise<boolean> {
    let isSuccessful: boolean = false;

    AppStoreModule.SET_ISLOADING(true);

    try {
      await adminService.cancelOnboardingRegistration(this.onboardingRegistration!.registrationId);
      isSuccessful = true;
    } catch (error) {
      AppStoreModule.SAVE_HTTP_ERROR('Unable to cancel registration');
    } finally {
      AppStoreModule.SET_ISLOADING(false);
    }

    return isSuccessful;
  }

  @Action
  async resetOdinSync(adminService: IAdminService): Promise<boolean> {
    let isSuccessful: boolean = false;

    try {
      await adminService.resetOnboardingRegistrationOdinSyncStatus(this.onboardingRegistration!.registrationId);
      isSuccessful = true;
    } catch (error) {
      AppStoreModule.SAVE_HTTP_ERROR('Unable to reset odin sync ');
    } 

    return isSuccessful;
  }


  @Action
  async requestCreate(adminService: IAdminService): Promise<boolean> {
    let isSuccessful: boolean = false;

    if (!this.form.doValidation) {
      this.SET_FORM_DO_VALIDATION(true);
      await Promise.all([
        this.UPDATE_NEW_CLOUD_ACCOUNT_FORM_STATE(),
        this.UPDATE_CUSTOMER_FORM_STATE(),
      ]);
    }

    if (this.isFormValid) {

      AppStoreModule.SET_ISLOADING(true);
      this.SET_ODIN_REFRESH_IS_DISABLED(true);

      try {

        const createNewOdinAccount: CreateNewOdinAccount = RegistrationsHelper.getCreateOdinAccount(this.form.data.newCloudAccount);

        await adminService.createOdinAccount(this.onboardingRegistration!.registrationId, createNewOdinAccount);

        isSuccessful = true;
      } catch (error) {
        AppStoreModule.SAVE_HTTP_ERROR('Unable to create Odin account');
      } finally {
        AppStoreModule.SET_ISLOADING(false);
      }
    }
    return isSuccessful;
  }

  @Action
  async requestSubmit(adminService: IAdminService): Promise<boolean> {
    let isSuccessful: boolean = false;

    if (!this.form.doValidation) {
      this.SET_FORM_DO_VALIDATION(true);
      await Promise.all([
        this.UPDATE_CLOUD_ACCOUNT_FORM_STATE(),
        this.UPDATE_CUSTOMER_FORM_STATE(),
      ]);
    }

    if (this.isFormValid) {
      AppStoreModule.SET_ISLOADING(true);

      try {
        const onboardingRegistration: OnboardingRegistration = RegistrationsHelper.getUpdateOnboardingRegistration(this.onboardingRegistration!, this.form.data);
        await adminService.updateOnboardingRegistration(onboardingRegistration);
        isSuccessful = true;
      } catch (error) {
        AppStoreModule.SAVE_HTTP_ERROR('Unable to submit registration');
      } finally {
        AppStoreModule.SET_ISLOADING(false);
      }
    }
    return isSuccessful;
  }

  @Action
  async submitOdinDataRefresh(adminService: IAdminService): Promise<void> {

    AppStoreModule.SET_ISLOADING(true);

    await adminService.refreshOdinAccounts(this.onboardingRegistration!.registrationId)
      .then(() => {
        this.SET_ODIN_REFRESH_IS_DISABLED(true);
      })
      .catch(() => {
        AppStoreModule.SAVE_HTTP_ERROR('Unable to refresh Odin data');
        this.SET_ODIN_REFRESH_IS_DISABLED(false);
      })
      .finally(() => {
        AppStoreModule.SET_ISLOADING(false);
      });

  }

  @Action
  async newCloudAccountFormInputChange(payload: { fieldName: string, value: any }) {
    this.UPDATE_NEW_CLOUD_ACCOUNT_FORM_FIELD_BY_NAME(payload);
    await this.UPDATE_NEW_CLOUD_ACCOUNT_FORM_STATE();
  }

  @Action
  getLookups() {
    // This can be changed to use an API to load the lookup data
    this.GET_STATEPROVINCES_LOOKUP();
    this.GET_COUNTRIES_LOOKUP();
  }

}
